import { useState } from "react";
import { Form, Formik } from "formik";
import {
  PRIMARY_ADDRESS_HI_COPY,
  PRIMARY_ADDRESS_UPSELL_COPY,
} from "pages/PreQualification/utils/constants";
import styles from "../PrimaryBusinessAddress.module.css";
import FooterButton from "uicomponents/FooterButton";
import BussinessAddressComponent from "uicomponents/AddressComponent/BusinessAddressComponent";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/PreQualification/utils/constants";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";
import { BusinessAddressInitialFormValues } from "uicomponents/AddressComponent/FormUtils";
import { SingleBusinessAddressType } from "types";
import { businessAddressValidationSchema } from "uicomponents/AddressComponent/FormUtils";
import { errorMessageMap, getAppType } from "utils/helper";
import { trackFormError } from "utils/analytics/trackEvent";
import { useUpdateBussinessAddressMutation } from "types/generated/operations";
import { usePreQualContext } from "pages/PreQualification/PreQualification";
import EditCard from "uicomponents/EditCard";
import SaveAddressList from "uicomponents/AddressComponent/SavedAddress";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import InvalidDialog from "./InvalidDialog";

interface IPrimaryAddressEditFormProps {
  data?: SingleBusinessAddressType;
  handleNextClick: () => void;
}

const PrimaryAddressEditForm = ({
  ...props
}: IPrimaryAddressEditFormProps): JSX.Element => {
  const { data, handleNextClick } = props;
  const { previousStep } = usePreQualContext();

  const [suggestedAddresses, setSuggestedAddresses] = useState<
    BusinessAddressFormType[]
  >([]);

  const addressCopy =
    getAppType() === "HEALTH_INSURANCE"
      ? PRIMARY_ADDRESS_HI_COPY
      : PRIMARY_ADDRESS_UPSELL_COPY;

  const initialFormValues: BusinessAddressFormType =
    BusinessAddressInitialFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [invalidWarning, setInvalidWarning] = useState(false);

  const [updateOfficeLocation, { loading: updateBussinessAddressLoading }] =
    useUpdateBussinessAddressMutation();

  const handleOnSubmit = async (values: BusinessAddressFormType) => {
    try {
      const response = await updateOfficeLocation({
        variables: {
          uuid: values.uuid,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          zip: values.zip,
          state: values.state,
          locationName: values.locationName,
          isHq: true,
          subdivision: values.subdivision,
        },
      });

      if (response?.data?.updateOfficeLocation?.autoValidated === false) {
        if (response?.data?.updateOfficeLocation?.suggestedAddress) {
          setSuggestedAddresses([
            {
              locationName: values.locationName,
              address1: values.address1,
              address2: values.address2,
              city: values.city,
              zip: values.zip,
              state: values.state,
              uuid: values.uuid,
            },
            {
              locationName: values.locationName,
              address1:
                response?.data?.updateOfficeLocation?.suggestedAddress
                  ?.address1,
              address2:
                response?.data?.updateOfficeLocation?.suggestedAddress
                  ?.address2 || "",
              city: response?.data?.updateOfficeLocation?.suggestedAddress
                ?.city,
              zip: response?.data?.updateOfficeLocation?.suggestedAddress?.zip,
              state:
                response?.data?.updateOfficeLocation?.suggestedAddress?.state,
              uuid: values.uuid,
            },
          ]);
        } else {
          setSuggestedAddresses([]);
          setInvalidWarning(true);
        }
        return;
      }

      handleNextClick();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    previousStep();
  };

  const handleKeepEntered = () => {
    setSuggestedAddresses([]);
    handleNextClick();
  };

  return (
    <>
      <InvalidDialog
        isOpen={invalidWarning}
        onContinue={() => {
          setInvalidWarning(false);
          handleNextClick();
        }}
        onReview={() => setInvalidWarning(false)}
      />

      {suggestedAddresses.length > 0 && (
        <SuggestedAddressModel
          enteredAddress={suggestedAddresses[0]}
          suggestedAddress={suggestedAddresses[1]}
          onUseSuggestion={handleOnSubmit}
          onKeepCurrent={handleKeepEntered}
        />
      )}
      <div className={styles.title}>{addressCopy.title}</div>
      <div className={styles.description}>{addressCopy.description}</div>
      {isEditing && (
        <Formik
          initialValues={initialFormValues}
          validationSchema={businessAddressValidationSchema}
          onSubmit={handleOnSubmit}
        >
          {() => (
            <>
              <Form>
                <EditCard
                  title={addressCopy.title}
                  showEditIcon={false}
                  description={addressCopy.openCardDescription}
                >
                  <BussinessAddressComponent
                    hasMargin={true}
                    isPrimaryAddress={true}
                  />
                </EditCard>
                <FooterButton
                  nextButtonText={
                    updateBussinessAddressLoading
                      ? LOADING_BUTTON_TEXT
                      : NEXT_BUTTON_TEXT
                  }
                  backButtonText={BACK_BUTTON_TEXT}
                  disabled={updateBussinessAddressLoading}
                  handleBackClick={handleBack}
                />
                {isError && <Alert message={errorMessage} />}
              </Form>
            </>
          )}
        </Formik>
      )}
      {!isEditing && (
        <>
          <EditCard
            title={addressCopy.title}
            onEditClick={() => setIsEditing(!isEditing)}
            showEditIcon={getAppType() === "HEALTH_INSURANCE"}
            hideBorder={true}
            description={addressCopy.closeCardDescription}
          >
            <SaveAddressList data={data} />
          </EditCard>
          <FooterButton
            nextButtonText={
              updateBussinessAddressLoading
                ? LOADING_BUTTON_TEXT
                : NEXT_BUTTON_TEXT
            }
            backButtonText={BACK_BUTTON_TEXT}
            handleBackClick={handleBack}
            handleNextClick={handleNextClick}
          />
          {isError && <Alert message={errorMessage} />}
        </>
      )}
    </>
  );
};
export default PrimaryAddressEditForm;
